import React, { useEffect, useState } from "react";

import { loadStripe, Stripe } from "@stripe/stripe-js";
import PricingPlans from "./PricingPlans";
import { useLocation } from "react-router-dom";
import UpgradeSuccess from "../../components/PricingCardSection/UpgradeSuccess";
import FailurePopup from "../../components/PricingCardSection/FailurePopup";

type AlertSeverity = "error" | "warning" | "info" | "success";

export default function ChatbotPricing() {
  const [isAnnual, setIsAnnual] = useState(true);

  const [snackbaropen, setsnackbaropen] = useState(false);
  const [snackmessage, setsnackmessage] = useState("Something went wrong!");
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<AlertSeverity>("error");

  const [selectedFrequency, setselectedFrequency] = useState("monthly");
  const [isPageLoading, setisPageLoading] = useState(false);
  const [openSuccessPopup, setOpenSuccessPopup] = useState(false);
  const [openFailurePopup, setOpenFailurePopup] = useState(false);

  const location = useLocation();

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setsnackbaropen(false);
  };

  let stripePromise: Promise<Stripe | null> | null = null;

  const getStripe = () => {
    if (!stripePromise) {
      stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY || "");
    }
    return stripePromise;
  };

  const handlePlanClick = async (plan: string) => {
    const planMapping: Record<string, string> = {
      "Starter AI": "professional",
      "Growth AI": "business",
      "Enterprise AI": "enterprise",
    };

    // console.log("Received plan : ", plan);
    const backendPlan = planMapping[plan];
    // console.log(selectedFrequency, plan);
    setisPageLoading(true);
    const successUrl = `${import.meta.env.VITE_APP_BASE_URL}${
      location.pathname
    }?payment_success=true`;
    const cancelUrl = `${import.meta.env.VITE_APP_BASE_URL}${
      location.pathname
    }?payment_success=false`;
    try {
      const res = await fetch(
        `${
          import.meta.env.VITE_LEGITT_PRO_BASE_URL
        }/api/LegittChat/payment/create-payment-session`,
        {
          method: "POST",
          body: JSON.stringify({
            // email: "",
            plan: backendPlan,
            frequency: selectedFrequency,
            success_url: `${successUrl}`,
            cancel_url: `${cancelUrl}`,
          }),
        }
      );

      const { sessionId } = await res.json();
      console.log("Stripe Session ID : ", sessionId);

      const stripe = await getStripe();
      if (!stripe) {
        setisPageLoading(false);
        setSnackbarSeverity("error");
        setsnackmessage("Somethig went wrong.");
        setsnackbaropen(true);
        console.log("Could not get Stripe Client");
        throw new Error("Stripe initialization failed.");
      }
      await stripe.redirectToCheckout({
        sessionId,
      });
    } catch (error) {
      setisPageLoading(false);
      setSnackbarSeverity("error");
      setsnackmessage("Something went wrong.");
      setsnackbaropen(true);
      console.log("Error occured in initiating the Stripe Payment.", error);
      console.log("Something went wrong!");
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentSuccess = params.get("payment_success");

    if (paymentSuccess === "true") {
      setOpenSuccessPopup(true);
    } else if (paymentSuccess === "false") {
      setOpenFailurePopup(true);
    }
  }, [location]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const hash = window.location.hash;
      if (hash) {
        const id = hash.substring(1); // Remove the '#' from the hash
        const section = document.getElementById(id);
        if (section) {
          section.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  }, []);

  return (
    <section className="mt-[100px]">
      <div className="bg-white">
        <div className="relative mx-auto max-w-7xl px-6 text-center lg:px-8 mb-2">
          <div className="mx-auto max-w-2xl lg:max-w-4xl">
            <h2 className="text-lg font-semibold leading-8 text-[#000]">
              Pricing
            </h2>
            <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900">
              Find the right plan for your project
            </p>
          </div>
        </div>
        <div className="mx-auto max-w-7xl px-6 lg:px-8 mb-6">
          <div className="flex justify-center">
            <div className="w-full flex mt-[20px] items-center justify-center ">
              <div className="bg-[#F2F2F2F2] flex p-1 gap-2 rounded-md font-medium">
                <div
                  className={`${
                    selectedFrequency === "monthly"
                      ? "bg-white text-[#000]"
                      : "bg-transparent  text-gray-500 text-[14px]"
                  } flex items-center justify-center rounded-md p-1 cursor-pointer transition-colors duration-300 ease-in-out w-[120px] `}
                  onClick={() => setselectedFrequency("monthly")}
                >
                  Monthly
                </div>
                <div
                  className={`${
                    selectedFrequency === "yearly"
                      ? "bg-white text-[#000]"
                      : "bg-transparent text-gray-500  text-[14px]"
                  } flex items-center justify-center rounded-md p-1 cursor-pointer transition-colors duration-300 ease-in-out w-[120px] `}
                  onClick={() => setselectedFrequency("yearly")}
                >
                  Yearly
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="">
          <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto grid max-w-md grid-cols-1 gap-8 lg:max-w-7xl lg:grid-cols-3 lg:gap-8">
              <PricingPlans
                selectedFrequency={selectedFrequency}
                isPageLoading={isPageLoading}
                handlePlanClick={handlePlanClick}
              />
            </div>
          </div>
        </div>
        <div className="m-6 mt-0">
          <div className="mx-auto max-w-7xl px-0 lg:px-8">
            <div className="mx-auto mt-[2rem] max-w-2xl rounded-3xl ring-1 shadow-xl ring-gray-200 lg:mx-0 lg:flex lg:max-w-none">
              <div className="p-8 sm:p-10 lg:flex-auto">
                <h3 className="text-2xl font-bold tracking-tight text-gray-900">
                  Customized Plan
                </h3>
                <p className="mt-2 text-base leading-7 text-gray-600">
                  Enjoy custom access to all our features:
                </p>
                <ul
                  role="list"
                  className="mt-8 grid grid-cols-1 gap-4 text-sm leading-6 text-gray-600 sm:grid-cols-2 sm:gap-6"
                >
                  <li className="flex gap-x-3">
                    <svg
                      className="h-6 w-5 flex-none text-black"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    Custom Chatbots
                  </li>
                  <li className="flex gap-x-3">
                    <svg
                      className="h-6 w-5 flex-none text-black"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    Custom AI Agents
                  </li>
                  <li className="flex gap-x-3">
                    <svg
                      className="h-6 w-5 flex-none text-black"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    Custom Human Agents
                  </li>
                  <li className="flex gap-x-3">
                    <svg
                      className="h-6 w-5 flex-none text-black"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    Custom Training File Pages
                  </li>
                  <li className="flex gap-x-3">
                    <svg
                      className="h-6 w-5 flex-none text-black"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    Custom Website Pages
                  </li>
                  <li className="flex gap-x-3">
                    <svg
                      className="h-6 w-5 flex-none text-black"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    Custom AI Messages
                  </li>
                  <li className="flex gap-x-3">
                    <svg
                      className="h-6 w-5 flex-none text-black"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    Custom Human Messages
                  </li>
                  <li className="flex gap-x-3">
                    <svg
                      className="h-6 w-5 flex-none text-black"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    Custom API Access
                  </li>
                </ul>
              </div>
              <div className="-mt-2 p-2 lg:mt-0 lg:w-full lg:max-w-md lg:flex-shrink-0">
                <div className="rounded-2xl bg-gray-50 py-10 text-center ring-1 ring-inset ring-gray-900/5 lg:flex lg:flex-col lg:justify-center lg:py-16">
                  <div className="mx-auto max-w-xs px-8">
                    <p className="mt-6 flex items-baseline justify-center gap-x-2">
                      <span className="text-5xl font-bold tracking-tight text-gray-900">
                        Custom
                      </span>
                    </p>
                    <a
                      href="/demo"
                      className="mt-10 block w-full rounded-md bg-[#06526d] px-3 py-2 text-center text-sm font-semibold text-white hover:bg-white hover:text-[#06526d] border-1 transition border-[#06526d] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      Contact Sales
                    </a>
                    <p className="mt-6 text-xs leading-5 text-gray-600">
                      Talk to us about your requirements and Create a custom
                      plan.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <UpgradeSuccess
        openSuccessPopup={openSuccessPopup}
        setOpenSuccessPopup={setOpenSuccessPopup}
        handleClosePopup={() => setOpenSuccessPopup(false)}
      />

      <FailurePopup
        openFailurePopup={openFailurePopup}
        setOpenFailurePopup={setOpenFailurePopup}
        handleClosePopup={() => setOpenFailurePopup(false)}
      />
    </section>
  );
}
